import { onUnmounted } from 'vue'
import { QueryClient, useQuery, useQueryClient, hashQueryKey } from '@tanstack/vue-query'
import { WebSocketConnection } from 'Common/utils/webSocketConnection'
import { WebSocketConnectionCache } from 'Common/utils/webSocketConnectionCache'

interface UseVueQuerySubscriptionOptions {
  url: string
  maxRetries: number
  retryInterval?: number
  queryKey: unknown[]
  select: (data: any) => void
}

/**
 * On message callback for useWebSocket to be passed to WebSocket object.
 * @param event WebSocket onerror event
 * @param key Key that relates websocket data to the queryClient. Also matches the key the WebSocketCacheEntry is stored at within WebsocketCache
 * @param queryClient QueryClient object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onMessageCallback(event: MessageEvent<any>, key: unknown[], queryClient: QueryClient) {
  try {
    const message = JSON.parse(event.data)
    const timeStamp = event.timeStamp
    const dataObj = {
      message: message,
      timeStamp: timeStamp,
    }
    queryClient.setQueryData(key, dataObj)
  } catch (error) {
    console.error('Error parsing WebSocket Message:', error)
    queryClient.setQueryData(key, error instanceof Error ? error.message : 'Error parsing data')
  }
}

//FIXME Correct typings between queryCache and webSocketConnectionCache
/**
 * On error callback for useWebSocket to be passed to WebSocket object.
 * @param event WebSocket onerror event
 * @param key Key that relates websocket data to the queryClient. Also matches the key the WebSocketCacheEntry is stored at within WebsocketCache
 * @param queryClient QueryClient object
 * @param webSocketConnectionCache WebSocketConnectionCache object
 */
function onErrorCallback(
  event: Event,
  key: unknown[],
  queryClient: QueryClient,
  webSocketConnectionCache: WebSocketConnectionCache
) {
  queryClient.setQueryData(key, `Error in establishing connection`)
  //TODO resolve typing issue between tanstack and websocket connnection cache
  webSocketConnectionCache.unsubscribe(key)
}

export function useWebSocket<Data>(options: UseVueQuerySubscriptionOptions) {
  const { url, maxRetries, retryInterval, queryKey, ...queryOptions } = options
  const hashedKey = hashQueryKey(queryKey)
  const queryClient = useQueryClient()
  const webSocketConnectionCache = new WebSocketConnectionCache()
  const webSocketConnection = new WebSocketConnection(
    url,
    null,
    null,
    (e) => onMessageCallback(e, queryKey, queryClient),
    (e) => onErrorCallback(e, queryKey, queryClient, webSocketConnectionCache),
    maxRetries,
    retryInterval,
    false
  )

  //Empty promise is resolved when queryData is set on WebSocket onMessage
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const query = useQuery(queryKey, () => new Promise<Data>(() => {}), queryOptions)

  const subscribe = () => {
    webSocketConnectionCache.subscribe(hashedKey, webSocketConnection)
  }

  const send = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
    webSocketConnection.send(data)
  }

  const close = (): void => {
    webSocketConnectionCache.unsubscribe(hashedKey)
  }

  const reconnect = (): void => {
    subscribe()
  }

  subscribe()
  onUnmounted(close)

  return { ...query, send, close, reconnect }
}
